import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Users,
  Home,
  Target,
  GitCompare,
  Trophy
} from "lucide-react";

export default function TeamsPage({ evaluator, onLogout }) {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [teamsAverages, setTeamsAverages] = useState([]);
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: "", description: "" });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [teamsRes, playersRes, averagesRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/players`),
        axios.get(`${API}/teams/all/averages`)
      ]);
      setTeams(teamsRes.data);
      setPlayers(playersRes.data);
      setTeamsAverages(averagesRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTeam = async (e) => {
    e.preventDefault();
    if (!newTeam.name) {
      toast.error("Digite o nome do time");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/teams`, newTeam);
      setTeams([...teams, response.data]);
      setNewTeam({ name: "", description: "" });
      toast.success("Time cadastrado com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cadastrar time");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o time ${teamName}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/teams/${teamId}`);
      setTeams(teams.filter(t => t.id !== teamId));
      toast.success("Time excluído com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir time");
    }
  };

  const handleAssignPlayer = async (playerId, teamId) => {
    try {
      const player = players.find(p => p.id === playerId);
      if (!player) return;
      
      await axios.put(`${API}/players/${playerId}`, {
        first_name: player.first_name || player.name?.split(' ')[0] || '',
        last_name: player.last_name || player.name?.split(' ').slice(1).join(' ') || '',
        position: player.position,
        yankee: player.yankee || '',
        photo_url: player.photo_url || '',
        team_id: teamId || null
      });
      
      setPlayers(players.map(p => p.id === playerId ? { ...p, team_id: teamId } : p));
      toast.success("Jogador atribuído ao time!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atribuir jogador");
    }
  };

  const getTeamAverage = (teamId) => {
    const avg = teamsAverages.find(t => t.team_id === teamId);
    if (!avg || avg.total_evaluations === 0) return 0;
    return (Object.values(avg.averages).reduce((a, b) => a + b, 0) / 7).toFixed(1);
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "text-green-400";
    if (score >= 5) return "text-yellow-400";
    return "text-red-400";
  };

  const getPlayerName = (player) => {
    if (player.first_name) return `${player.first_name} ${player.last_name}`;
    return player.name || '';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                Gerenciar Times
              </h1>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/teams/compare')}
              data-testid="compare-teams-btn"
            >
              <GitCompare className="mr-2 h-4 w-4" />
              Comparar Times
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Add Team Form */}
          <Card className="bg-card border-border/50 shadow-xl lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Criar Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddTeam} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Nome do Time
                  </Label>
                  <Input
                    type="text"
                    placeholder="Ex: Time A"
                    className="bg-input/50 border-transparent focus:border-primary"
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                    required
                    data-testid="team-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Descrição (opcional)
                  </Label>
                  <Input
                    type="text"
                    placeholder="Descrição do time"
                    className="bg-input/50 border-transparent focus:border-primary"
                    value={newTeam.description}
                    onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                    data-testid="team-description-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,122,255,0.3)] font-bold uppercase tracking-wider"
                  disabled={isSubmitting}
                  data-testid="add-team-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {isSubmitting ? "Criando..." : "Criar Time"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Teams List */}
          <Card className="bg-card border-border/50 shadow-xl lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-400" />
                Times ({teams.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">Carregando...</div>
              ) : teams.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum time cadastrado ainda.
                </div>
              ) : (
                <div className="space-y-4">
                  {teams.map((team, index) => {
                    const teamAvg = getTeamAverage(team.id);
                    const teamPlayers = players.filter(p => p.team_id === team.id);
                    
                    return (
                      <div 
                        key={team.id}
                        className="p-4 rounded-xl bg-secondary/30 border border-border/50"
                        data-testid={`team-card-${index}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-bold text-lg">{team.name}</h3>
                            {team.description && (
                              <p className="text-sm text-muted-foreground">{team.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Média</p>
                              <p className={`text-2xl font-black ${getScoreColor(parseFloat(teamAvg))}`}>
                                {teamAvg}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleDeleteTeam(team.id, team.name)}
                              data-testid={`delete-team-${index}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {teamPlayers.length} jogadores
                          </span>
                        </div>
                        
                        {teamPlayers.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {teamPlayers.map(player => (
                              <Badge key={player.id} variant="secondary">
                                {getPlayerName(player)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Assign Players to Teams */}
        <Card className="bg-card border-border/50 shadow-xl mt-8">
          <CardHeader>
            <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Atribuir Jogadores aos Times
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-xs font-bold uppercase tracking-widest">Jogador</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest">Posição</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest">Time Atual</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest">Atribuir a</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {players.map((player, index) => (
                    <TableRow key={player.id} className="border-border/50">
                      <TableCell className="font-medium">{getPlayerName(player)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{player.position}</Badge>
                      </TableCell>
                      <TableCell>
                        {player.team_id ? (
                          <Badge>{teams.find(t => t.id === player.team_id)?.name || 'Time Removido'}</Badge>
                        ) : (
                          <span className="text-muted-foreground">Sem time</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={player.team_id || "none"} 
                          onValueChange={(value) => handleAssignPlayer(player.id, value === "none" ? null : value)}
                        >
                          <SelectTrigger className="w-40 bg-input/50" data-testid={`assign-team-${index}`}>
                            <SelectValue placeholder="Selecionar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem time</SelectItem>
                            {teams.map(team => (
                              <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Home className="h-5 w-5" />
          <span className="text-xs">Início</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/teams')}
          className="flex flex-col items-center gap-1 h-auto py-2 text-primary"
        >
          <Trophy className="h-5 w-5" />
          <span className="text-xs">Times</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/evaluate')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Target className="h-5 w-5" />
          <span className="text-xs">Avaliar</span>
        </Button>
      </nav>
    </div>
  );
}
